import { Entity } from "@common/types/entity";
import { BulkUpdateParams } from "../../../transactions/TransactionsBulkActions/lib";
import {
    extractRowActionKey,
    extractRowActionValue,
    stringifyActionValue,
} from "./seedActionRow";
import { ActionRowDefinition } from "./types";
import {
    FetchMatchingTransactionIdsForConditions,
    getBulkValueForAction,
} from "./applyActionsPlanner";
import { resolveRowConditions } from "./rowMatching";

export interface DeleteActionReassignment {
    transactionId: number;
    value: BulkUpdateParams["value"];
}

interface BuildDeleteActionReassignmentsParams {
    rows: ActionRowDefinition[];
    deletedRowId: string;
    rowIdToOwnedTxIds: Map<string, number[]>;
    scopeConditions: ActionRowDefinition["conditions"];
    valueToAssign: BulkUpdateParams;
    entities: Entity[];
    fetchMatchingTransactionIdsForConditions: FetchMatchingTransactionIdsForConditions;
}

interface BuildDeleteBaseActionReassignmentsParams {
    rows: ActionRowDefinition[];
    deletedRowId: string;
    newValuesMap: Record<number, BulkUpdateParams["value"]>;
    valueToAssign: BulkUpdateParams;
    entities: Entity[];
}

interface ReassignmentCandidate {
    row: ActionRowDefinition;
    value: BulkUpdateParams["value"];
}

function getValidCandidate({
    row,
    valueToAssign,
    entities,
}: {
    row: ActionRowDefinition;
    valueToAssign: BulkUpdateParams;
    entities: Entity[];
}): ReassignmentCandidate | undefined {
    const rowValue = extractRowActionValue(valueToAssign.update, row);
    if (rowValue === undefined) {
        return undefined;
    }

    const value = getBulkValueForAction({
        update: valueToAssign.update,
        value: rowValue,
        entities,
    });
    if (value === undefined) {
        return undefined;
    }

    return { row, value };
}

function getFirstValidCandidate({
    rows,
    valueToAssign,
    entities,
}: {
    rows: ActionRowDefinition[];
    valueToAssign: BulkUpdateParams;
    entities: Entity[];
}): ReassignmentCandidate | undefined {
    for (const row of rows) {
        const candidate = getValidCandidate({ row, valueToAssign, entities });
        if (candidate) {
            return candidate;
        }
    }
    return undefined;
}

function getPromotedRowAfterDelete({
    rows,
    deletedIndex,
    deletedRowId,
}: {
    rows: ActionRowDefinition[];
    deletedIndex: number;
    deletedRowId: string;
}): ActionRowDefinition | undefined {
    const remainingRows = rows.filter((row) => row.id !== deletedRowId);
    if (remainingRows.length === 0) {
        return undefined;
    }
    const promotedIndex =
        deletedIndex < remainingRows.length ? deletedIndex : 0;
    return remainingRows[promotedIndex];
}

export function buildDeleteBaseActionReassignments({
    rows,
    deletedRowId,
    newValuesMap,
    valueToAssign,
    entities,
}: BuildDeleteBaseActionReassignmentsParams): DeleteActionReassignment[] {
    const deletedIndex = rows.findIndex((row) => row.id === deletedRowId);
    const deletedRow = deletedIndex >= 0 ? rows[deletedIndex] : undefined;
    if (!deletedRow?.isBaseAction) {
        return [];
    }

    const deletedKey = extractRowActionKey(valueToAssign.update, deletedRow);
    if (deletedKey === undefined) {
        return [];
    }

    const promotedRow = getPromotedRowAfterDelete({
        rows,
        deletedIndex,
        deletedRowId,
    });
    if (!promotedRow) {
        return [];
    }

    const target = getValidCandidate({
        row: promotedRow,
        valueToAssign,
        entities,
    });
    if (!target) {
        return [];
    }

    const assignments: DeleteActionReassignment[] = [];
    for (const [transactionId, value] of Object.entries(newValuesMap)) {
        const key = stringifyActionValue(valueToAssign.update, value);
        if (key !== deletedKey) {
            continue;
        }
        assignments.push({
            transactionId: Number(transactionId),
            value: target.value,
        });
    }
    return assignments;
}

/**
 * Reassigns the deleted row's current bucket before the row is removed.
 * Condition rows after the deleted row get first claim; otherwise deleted
 * transactions fall through to the first available action below the row.
 */
export async function buildDeleteActionReassignments({
    rows,
    deletedRowId,
    rowIdToOwnedTxIds,
    scopeConditions,
    valueToAssign,
    entities,
    fetchMatchingTransactionIdsForConditions,
}: BuildDeleteActionReassignmentsParams): Promise<DeleteActionReassignment[]> {
    const deletedIndex = rows.findIndex((row) => row.id === deletedRowId);
    const deletedRow = deletedIndex >= 0 ? rows[deletedIndex] : undefined;
    if (!deletedRow || deletedRow.isBaseAction) {
        return [];
    }

    const deletedTransactionIds = rowIdToOwnedTxIds.get(deletedRowId) ?? [];
    if (deletedTransactionIds.length === 0) {
        return [];
    }

    const rowsAfterDeleted = rows.slice(deletedIndex + 1);
    const remainingRows = rows.filter((row) => row.id !== deletedRowId);
    const fallbackCandidate =
        getFirstValidCandidate({
            rows: rowsAfterDeleted,
            valueToAssign,
            entities,
        }) ??
        getFirstValidCandidate({
            rows: remainingRows,
            valueToAssign,
            entities,
        });

    const conditionCandidates = rowsAfterDeleted
        .map((row) => getValidCandidate({ row, valueToAssign, entities }))
        .filter(
            (candidate): candidate is ReassignmentCandidate =>
                candidate !== undefined && candidate.row.conditions.length > 0,
        );

    const candidateMatches = await Promise.all(
        conditionCandidates.map(async (candidate) => {
            const conditions = resolveRowConditions({
                row: candidate.row,
                scopeConditions,
            });
            const matchingTransactionIds =
                conditions.length === 0
                    ? new Set<number>()
                    : await fetchMatchingTransactionIdsForConditions(
                          conditions,
                      );
            return { candidate, matchingTransactionIds };
        }),
    );

    const assignments: DeleteActionReassignment[] = [];
    for (const transactionId of deletedTransactionIds) {
        let target = fallbackCandidate;
        for (const { candidate, matchingTransactionIds } of candidateMatches) {
            if (matchingTransactionIds.has(transactionId)) {
                target = candidate;
                break;
            }
        }
        if (!target) {
            continue;
        }
        assignments.push({ transactionId, value: target.value });
    }

    return assignments;
}
